import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as invoicesApi from '../api/invoices';
import * as clientsApi from '../api/clients';
import * as templatesApi from '../api/templates';
import 'react-quill-new/dist/quill.snow.css';

// Import Quote Builder components (reused for invoices)
import QuoteSectionCard from '../features/quote-builder/QuoteSectionCard';
import QuoteCalculationsDisplay from '../features/quote-builder/QuoteCalculationsDisplay';
import QuoteSectionEditorModal from '../features/quote-builder/QuoteSectionEditorModal';
import TemplateSelectionModal from '../features/quote-builder/TemplateSelectionModal';
import PaymentScheduleBuilder from '../components/PaymentScheduleBuilder';
import PaymentRecordingModal from '../components/PaymentRecordingModal';

function InvoiceFormPage() {
    const { loading: authLoading, user } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!id;

    const [formData, setFormData] = useState({
        clientId: '',
        title: '',
        description: '',
        overallTaxRate: 0.00,
        discountType: 'percentage',
        discountValue: 0,
        notes: '',
        sections: [],
        status: 'draft',
        quoteId: null,
        publicAccessToken: null
    });

    const [paymentSchedule, setPaymentSchedule] = useState([]);
    const [clients, setClients] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [paymentSummary, setPaymentSummary] = useState(null);

    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(true);

    const [isSectionEditorModalOpen, setIsSectionEditorModalOpen] = useState(false);
    const [isTemplateSelectorModalOpen, setIsTemplateSelectorModalOpen] = useState(false);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [currentEditingSectionId, setCurrentEditingSectionId] = useState(null);

    useEffect(() => {
        const fetchInitialData = async () => {
            if (authLoading) {
                setLoading(false);
                return;
            }

            try {
                setError(null);
                setSuccess(null);
                setLoading(true);

                const clientsResponse = await clientsApi.fetchClients();
                setClients(clientsResponse.clients || []);

                const templatesResponse = await templatesApi.fetchTemplates();
                setTemplates(templatesResponse.templates || []);

                if (isEditing) {
                    const invoiceResponse = await invoicesApi.fetchInvoiceById(id);
                    const invoiceData = invoiceResponse.invoice;

                    // Helper function to convert any date format to yyyy-MM-dd
                    const formatDateForInput = (dateString) => {
                        if (!dateString) return '';
                        const date = new Date(dateString);
                        if (isNaN(date.getTime())) return '';
                        return date.toISOString().split('T')[0];
                    };

                    setFormData({
                        clientId: invoiceData.client_id || '',
                        title: invoiceData.title || '',
                        description: invoiceData.description || '',
                        overallTaxRate: parseFloat(invoiceData.tax_rate) || 0,
                        discountType: 'percentage',
                        discountValue: 0,
                        notes: invoiceData.notes || '',
                        sections: invoiceData.sections || [],
                        status: invoiceData.status || 'draft',
                        quoteId: invoiceData.quote_id || null,
                        publicAccessToken: invoiceData.public_access_token || null
                    });

                    // Fetch payment schedule
                    const paymentsResponse = await invoicesApi.fetchInvoicePayments(id);
                    setPaymentSchedule(paymentsResponse.payments || []);
                    
                    // Fetch payment summary for existing invoices
                    try {
                        const summaryResponse = await invoicesApi.getInvoicePaymentStatus(id);
                        setPaymentSummary(summaryResponse.paymentStatus);
                    } catch (summaryError) {
                        console.error('Error loading payment summary:', summaryError);
                        // Don't fail the page load if payment summary fails
                    }
                } else {
                    // For new invoices, create default payment schedule if none exists
                    const defaultDueDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                    setFormData(prev => ({
                        ...prev,
                        notes: user?.defaultTermsConditions || ''
                    }));
                    
                    // Create default payment schedule (full amount, 30 days)
                    setPaymentSchedule([{
                        id: Date.now(),
                        payment_number: 1,
                        description: 'Payment Due',
                        amount: 0, // Will be updated when totals change
                        due_type: 'date',
                        due_date: defaultDueDate,
                        milestone: null,
                        notes: null
                    }]);
                }
            } catch (err) {
                console.error('Failed to load initial data:', err);
                setError(err.message || 'Failed to load initial data for invoice form.');
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [id, isEditing, authLoading, user?.defaultTermsConditions]);

    // Calculate totals (reuse quote calculation logic)
    const calculateTotals = () => {
        let subtotal = 0;
        let totalTaxAmount = 0;

        formData.sections.forEach(section => {
            if (Array.isArray(section.items)) {
                section.items.forEach(item => {
                    // Calculate itemTotal from unitPrice * quantity (more reliable than item.total)
                    const itemTotal = (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0);
                    subtotal += itemTotal;

                    const itemTaxRate = parseFloat(item.taxRate) || parseFloat(formData.overallTaxRate) || 0;
                    const itemTaxAmount = (itemTotal * itemTaxRate) / 100;
                    totalTaxAmount += itemTaxAmount;
                });
            }
        });

        let discountAmount = 0;
        if (formData.discountType === 'percentage') {
            discountAmount = (subtotal * parseFloat(formData.discountValue || 0)) / 100;
        } else {
            discountAmount = parseFloat(formData.discountValue || 0);
        }

        const total = subtotal + totalTaxAmount - discountAmount;

        return {
            subtotal: subtotal.toFixed(2),
            taxAmount: totalTaxAmount.toFixed(2),
            discountAmount: discountAmount.toFixed(2),
            total: total.toFixed(2)
        };
    };

    const totals = calculateTotals();

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    // Update payment schedule when totals change (only for unset amounts)
    useEffect(() => {
        if (paymentSchedule.length > 0 && totals.total > 0) {
            // Only update payments that have amount 0 (unset) to avoid interfering with user edits
            const hasUnsetPayments = paymentSchedule.some(payment => payment.amount === 0);
            
            if (hasUnsetPayments) {
                const updatedSchedule = paymentSchedule.map(payment => {
                    // Only update payments with amount 0 (unset)
                    if (payment.amount === 0) {
                        return {
                            ...payment,
                            amount: totals.total / paymentSchedule.length
                        };
                    }
                    return payment;
                });
                
                setPaymentSchedule(updatedSchedule);
            }
        }
    }, [totals.total]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handlePaymentScheduleChange = (payments) => {
        setPaymentSchedule(payments);
    };

    const refreshPaymentData = async () => {
        if (id) {
            try {
                const paymentsResponse = await invoicesApi.fetchInvoicePayments(id);
                setPaymentSchedule(paymentsResponse.payments || []);
                
                // Also refresh payment summary
                try {
                    const summaryResponse = await invoicesApi.getInvoicePaymentStatus(id);
                    setPaymentSummary(summaryResponse.paymentStatus);
                } catch (summaryError) {
                    console.error('Error refreshing payment summary:', summaryError);
                }
            } catch (error) {
                console.error('Error refreshing payment data:', error);
            }
        }
    };

    const handleSubmit = async (e, shouldNavigateBack = true) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // Calculate main due date from payment schedule (earliest payment due date)
            const dueDate = paymentSchedule.length > 0 
                ? paymentSchedule.reduce((earliest, payment) => {
                    const paymentDate = new Date(payment.due_date);
                    const earliestDate = new Date(earliest);
                    return paymentDate < earliestDate ? payment.due_date : earliest;
                }, paymentSchedule[0].due_date)
                : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // Default 30 days from now

            const invoiceData = {
                ...formData,
                subtotal: parseFloat(totals.subtotal),
                taxRate: parseFloat(formData.overallTaxRate),
                total: parseFloat(totals.total),
                dueDate: dueDate,
                payments: paymentSchedule
            };

            if (isEditing) {
                await invoicesApi.updateInvoice(id, invoiceData);
                setSuccess('Invoice updated successfully!');
            } else {
                const response = await invoicesApi.createInvoice(invoiceData);
                setSuccess('Invoice created successfully!');
                
                // Update formData with the new invoice ID and public access token
                setFormData(prev => ({
                    ...prev,
                    id: response.invoice.id,
                    publicAccessToken: response.invoice.publicAccessToken
                }));
                
                if (shouldNavigateBack) {
                    navigate(`/invoices/edit/${response.invoice.id}`);
                    return;
                }
            }

            if (shouldNavigateBack) {
                setTimeout(() => navigate('/invoices'), 2000);
            }
        } catch (err) {
            console.error('Failed to save invoice:', err);
            setError(err.message || 'Failed to save invoice.');
        } finally {
            setLoading(false);
        }
    };

    // Section management functions (reuse from quote form)
    const addSection = () => {
        const newSection = {
            id: Date.now(),
            name: `Section ${formData.sections.length + 1}`,
            description: '',
            items: [
                {
                    id: Date.now() + 1,
                    itemName: 'Labor',
                    description: 'Labor hours',
                    quantity: 0,
                    unitType: 'unit',
                    unitPrice: 1,
                    total: 0,
                    taxRate: 0,
                    isTaxable: true
                },
                {
                    id: Date.now() + 2,
                    itemName: 'Materials',
                    description: 'Materials and supplies',
                    quantity: 0,
                    unitType: 'unit',
                    unitPrice: 1,
                    total: 0,
                    taxRate: 0,
                    isTaxable: true
                }
            ]
        };
        setFormData(prev => ({
            ...prev,
            sections: [...prev.sections, newSection]
        }));
    };

    const updateSection = (sectionId, updatedSection) => {
        setFormData(prev => ({
            ...prev,
            sections: prev.sections.map(section =>
                section.id === sectionId ? updatedSection : section
            )
        }));
    };

    const deleteSection = (sectionId) => {
        if (window.confirm('Are you sure you want to delete this section?')) {
            setFormData(prev => ({
                ...prev,
                sections: prev.sections.filter(section => section.id !== sectionId)
            }));
        }
    };

    const duplicateSection = (sectionId) => {
        setFormData(prev => {
            const sectionToDuplicate = prev.sections.find(s => s.id === sectionId);
            if (!sectionToDuplicate) return prev;

            const newId = Date.now();
            const duplicatedSection = {
                ...sectionToDuplicate,
                id: newId,
                name: `${sectionToDuplicate.name} (Copy)`,
                items: sectionToDuplicate.items.map(item => ({ ...item, id: Math.random() }))
            };

            return {
                ...prev,
                sections: [...prev.sections, duplicatedSection]
            };
        });
    };

    const calculateSectionTotalsForDisplay = (section) => {
        let subtotal = 0;
        if (Array.isArray(section.items)) {
            section.items.forEach(item => {
                const itemTotal = (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0);
                subtotal += itemTotal;
            });
        }
        return { subtotal };
    };

    const openSectionEditor = (sectionId = null) => {
        setCurrentEditingSectionId(sectionId);
        setIsSectionEditorModalOpen(true);
    };

    const handleTemplateSelect = async (templateId) => {
        try {
            // Fetch the full template data with items
            const templateResponse = await templatesApi.fetchTemplateById(null, templateId);
            const template = templateResponse.template;
            
            // Create section from template
            const newSection = {
                id: Date.now(),
                name: template.name || 'Template Section',
                description: template.description || '',
                items: (template.items || []).map(item => ({
                    id: Date.now() + Math.random(),
                    itemName: item.itemName || item.item_name || '',
                    description: item.description || '',
                    quantity: 0,
                    unitType: item.unitType || item.unit_type || 'unit',
                    unitPrice: parseFloat(item.unitPrice || item.unit_price || 1),
                    total: 0,
                    taxRate: 0,
                    isTaxable: Boolean(item.isTaxable !== undefined ? item.isTaxable : item.is_taxable !== undefined ? item.is_taxable : true)
                }))
            };
            
            setFormData(prev => ({
                ...prev,
                sections: [...prev.sections, newSection]
            }));
            setIsTemplateSelectorModalOpen(false);
        } catch (error) {
            console.error('Failed to load template:', error);
            // Fallback to empty section with default items
            const newSection = {
                id: Date.now(),
                name: `Template Section`,
                description: '',
                items: [
                    {
                        id: Date.now() + 1,
                        itemName: 'Labor',
                        description: 'Labor hours',
                        quantity: 1,
                        unitType: 'hr',
                        unitPrice: 0,
                        total: 0,
                        taxRate: 0,
                        isTaxable: true
                    },
                    {
                        id: Date.now() + 2,
                        itemName: 'Materials',
                        description: 'Materials and supplies',
                        quantity: 1,
                        unitType: 'ea',
                        unitPrice: 0,
                        total: 0,
                        taxRate: 0,
                        isTaxable: true
                    }
                ]
            };
            setFormData(prev => ({
                ...prev,
                sections: [...prev.sections, newSection]
            }));
            setIsTemplateSelectorModalOpen(false);
        }
    };

    const handleAddCustomSection = () => {
        const newSection = {
            id: Date.now(),
            name: `Section ${formData.sections.length + 1}`,
            description: '',
            items: [
                {
                    id: Date.now() + 1,
                    itemName: 'Labor',
                    description: 'Labor hours',
                    quantity: 0,
                    unitType: 'unit',
                    unitPrice: 1,
                    total: 0,
                    taxRate: 0,
                    isTaxable: true
                },
                {
                    id: Date.now() + 2,
                    itemName: 'Materials',
                    description: 'Materials and supplies',
                    quantity: 0,
                    unitType: 'unit',
                    unitPrice: 1,
                    total: 0,
                    taxRate: 0,
                    isTaxable: true
                }
            ]
        };
        setFormData(prev => ({
            ...prev,
            sections: [...prev.sections, newSection]
        }));
        setIsTemplateSelectorModalOpen(false);
    };

    // --- Send Invoice via Email ---
    const handleSendInvoice = async () => {
        if (!formData || !formData.clientId) {
            alert("Error: Please select a client for this invoice.");
            return;
        }

        if (!user || !user.companyName) {
            alert("Error: Company details not found for the current user.");
            return;
        }
        
        const selectedClient = clients.find(c => c.id === parseInt(formData.clientId));
        if (!selectedClient || !selectedClient.email) {
            alert("Error: The selected client does not have an email address.");
            return;
        }

        // For new invoices, save first before sending
        if (!isEditing || !id) {
            alert("Please save the invoice before sending.");
            return;
        }

        // Check if publicAccessToken is available
        if (!formData.publicAccessToken) {
            alert("Error: Invoice public access token not found. Please refresh the page and try again.");
            return;
        }

        const publicUrl = `https://swiftbidpro.com/invoices/public/${formData.publicAccessToken}`;
        
        const emailData = {
            recipientEmail: selectedClient.email,
            subject: `Invoice from ${user.companyName}`,
            bodyText: `Hello ${selectedClient.name},\n\nPlease review your invoice by clicking the link below:\n${publicUrl}\n\nInvoice Details:\n- Total: $${totals.total}\n\nThank you for your business!\n\n${user.companyName}`
        };

        try {
            setLoading(true);
            await invoicesApi.sendInvoice(id, emailData);
            setSuccess('Invoice sent successfully!');
        } catch (error) {
            console.error("Failed to send invoice:", error);
            setError(`Error sending invoice: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    if (authLoading || loading) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 p-4">
                <div className="text-xl text-gray-700">
                    {isEditing ? 'Loading Invoice...' : 'Loading Invoice Form...'}
                </div>
            </div>
        );
    }

    if (error && !formData.title) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 p-4">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md max-w-md text-center">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-200 mb-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-3xl font-bold text-primary-700">
                            {isEditing ? 'Edit Invoice' : 'Create New Invoice'}
                        </h2>
                        <Link
                            to="/invoices"
                            className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition duration-300"
                        >
                            ← Back to Invoices
                        </Link>
                    </div>

                    {success && (
                        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md mb-4">
                            {success}
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4">
                            {error}
                        </div>
                    )}

                    {/* Basic Invoice Info */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">
                                    Client *
                                </label>
                                <select
                                    id="clientId"
                                    name="clientId"
                                    value={formData.clientId}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                                >
                                    <option value="">Select a client...</option>
                                    {clients.map(client => (
                                        <option key={client.id} value={client.id}>
                                            {client.name} - {client.email}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                                    Invoice Title *
                                </label>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                                    placeholder="Enter invoice title..."
                                />
                            </div>

                            {/* Due date removed - now determined by payment schedule */}

                            <div className="md:col-span-2">
                                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                                    Description
                                </label>
                                <textarea
                                    id="description"
                                    name="description"
                                    rows="3"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                                    placeholder="Brief description of the work or services..."
                                />
                            </div>
                        </div>

                        {/* Template Selection */}
                        <div className="border-t border-gray-200 pt-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Invoice Items</h3>
                                <div className="flex space-x-2">
                                    <button
                                        type="button"
                                        onClick={() => setIsTemplateSelectorModalOpen(true)}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50"
                                    >
                                        Use Template
                                    </button>
                                    <button
                                        type="button"
                                        onClick={addSection}
                                        className="px-4 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700"
                                    >
                                        + Add Section
                                    </button>
                                </div>
                            </div>

                            {/* Sections */}
                            <div className="space-y-4">
                                {formData.sections.map((section, index) => (
                                    <QuoteSectionCard
                                        key={section.id}
                                        section={section}
                                        onEdit={() => openSectionEditor(section.id)}
                                        onRemove={() => deleteSection(section.id)}
                                        onDuplicate={() => duplicateSection(section.id)}
                                        calculateSectionTotalsForDisplay={calculateSectionTotalsForDisplay}
                                    />
                                ))}

                                {formData.sections.length === 0 && (
                                    <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                                        <p className="text-gray-500 mb-4">No sections added yet</p>
                                        <button
                                            type="button"
                                            onClick={addSection}
                                            className="px-4 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700"
                                        >
                                            Add Your First Section
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Tax and Calculations */}
                        <div className="border-t border-gray-200 pt-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="overallTaxRate" className="block text-sm font-medium text-gray-700 mb-1">
                                        Overall Tax Rate (%)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        id="overallTaxRate"
                                        name="overallTaxRate"
                                        value={formData.overallTaxRate}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                                    />
                                </div>
                                <QuoteCalculationsDisplay
                                    totals={{
                                        subtotal: totals.subtotal,
                                        taxableSubtotal: totals.subtotal, // For invoices, all items are typically taxable
                                        calculatedTaxAmount: totals.taxAmount,
                                        calculatedDiscountAmount: totals.discountAmount,
                                        grandTotal: totals.total
                                    }}
                                />
                                
                                {/* Payment Summary for existing invoices */}
                                {isEditing && paymentSummary && (
                                    <div className="mt-3 space-y-1">
                                        <div className="flex justify-between items-center text-gray-700">
                                            <span>Amount Paid:</span>
                                            <span className="text-green-600 font-medium">-{formatCurrency(paymentSummary.totalPaid)}</span>
                                        </div>
                                        <div className="flex justify-between items-center text-gray-700">
                                            <span>Amount Due:</span>
                                            <span className={`font-medium ${
                                                paymentSummary.balance > 0 ? 'text-red-600' : 
                                                paymentSummary.balance < 0 ? 'text-blue-600' : 'text-green-600'
                                            }`}>
                                                {formatCurrency(paymentSummary.balance)}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Payment Schedule */}
                        <div className="border-t border-gray-200 pt-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-gray-900">Payment Schedule</h3>
                                {isEditing && (
                                    <button
                                        type="button"
                                        onClick={() => setIsPaymentModalOpen(true)}
                                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                    >
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                        Record Payment
                                    </button>
                                )}
                            </div>
                            <PaymentScheduleBuilder
                                payments={paymentSchedule}
                                totalAmount={parseFloat(totals.total)}
                                onPaymentsChange={handlePaymentScheduleChange}
                            />
                        </div>

                        {/* Notes */}
                        <div className="border-t border-gray-200 pt-6">
                            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                                Invoice Notes & Terms
                            </label>
                            <textarea
                                id="notes"
                                name="notes"
                                rows="6"
                                value={formData.notes}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Payment terms, warranty information, and other important details..."
                            />
                        </div>

                        {/* Form Actions */}
                        <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
                            <Link
                                to="/invoices"
                                className="w-full sm:w-auto text-center px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50"
                            >
                                Cancel
                            </Link>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full sm:w-auto px-6 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Saving...' : (isEditing ? 'Update Invoice' : 'Create Invoice')}
                            </button>
                            {(isEditing || formData.clientId) && (
                                <button
                                    type="button"
                                    onClick={handleSendInvoice}
                                    disabled={loading || !formData.clientId}
                                    className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Send Invoice
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>

            {/* Modals */}
            <QuoteSectionEditorModal
                isOpen={isSectionEditorModalOpen}
                onClose={() => setIsSectionEditorModalOpen(false)}
                initialLineItems={formData.sections.find(s => s.id === currentEditingSectionId)?.items || []}
                initialSectionName={formData.sections.find(s => s.id === currentEditingSectionId)?.name || ''}
                initialSectionDescription={formData.sections.find(s => s.id === currentEditingSectionId)?.description || ''}
                onSave={(sectionData) => {
                    const updatedSection = {
                        id: currentEditingSectionId,
                        name: sectionData.name,
                        description: sectionData.description,
                        items: sectionData.items
                    };
                    updateSection(currentEditingSectionId, updatedSection);
                }}
                overallTaxRate={formData.overallTaxRate}
            />

            <TemplateSelectionModal
                isOpen={isTemplateSelectorModalOpen}
                onClose={() => setIsTemplateSelectorModalOpen(false)}
                templates={templates}
                onSelectTemplate={handleTemplateSelect}
                onAddCustomSection={handleAddCustomSection}
            />

            <PaymentRecordingModal
                isOpen={isPaymentModalOpen}
                onClose={() => setIsPaymentModalOpen(false)}
                invoiceId={id}
                paymentSchedule={paymentSchedule}
                onPaymentRecorded={refreshPaymentData}
            />
        </div>
    );
}

export default InvoiceFormPage;